import { useEffect, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { getJson, postJson } from "../api";
import { useTexts } from "../texts";
import { AuthPage, Field, Loading, Notice, PasswordInput, showResult } from "../ui/kit";

type Reply = {
  status: "changed" | "invalid" | "error" | "expired";
  next?: string;
  messageKey?: string;
  fields?: { field: string; messageKey: string }[];
};

/**
 * Cambio obligatorio de contraseña (CambiarPassword.razor): llega desde el ingreso con un token firmado de 10 minutos,
 * sin sesión. Pide la contraseña actual y la nueva; al cambiarla vuelve al ingreso con «Contraseña actualizada».
 */
export function ChangePasswordPage() {
  const { L } = useTexts();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [reason, setReason] = useState<string | null | undefined>(undefined);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const current = useRef<HTMLInputElement>(null);
  const next = useRef<HTMLInputElement>(null);
  useEffect(() => {
    document.title = `${L("PWD_CHANGE_TITLE")} - Bheli Admin`;
  }, [L]);
  // Un token vencido o ajeno vuelve al ingreso, como el portal actual.
  useEffect(() => {
    void getJson<{ reason: string | null }>(
      `/api/admin/session/change-password?token=${encodeURIComponent(token ?? "")}`,
    ).then((reply) => {
      if (reply.status !== 200 || !reply.body?.reason) window.location.replace("/login?error=expired");
      else setReason(reply.body.reason);
    });
  }, [token]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const reply = await postJson<Reply>("/api/admin/session/change-password", {
        token,
        currentPassword: current.current?.value ?? "",
        newPassword: next.current?.value ?? "",
        returnUrl: params.get("ReturnUrl"),
      }).catch(() => null);
      const body = reply?.body;
      if (body?.next && (body.status === "changed" || body.status === "expired")) {
        window.location.assign(body.next);
        return;
      }
      const issues = body?.fields ?? [];
      showResult(() => {
        setFields(Object.fromEntries(issues.map((issue) => [issue.field, L(issue.messageKey)])));
        setError(issues.length > 0 ? null : L(body?.messageKey ?? "PWD_ERR_GENERIC"));
      }, issues.length > 0);
    } finally {
      setSending(false);
    }
  };

  if (reason === undefined)
    return (
      <AuthPage title={L("PWD_CHANGE_TITLE")} product={L("ADM_PRODUCT")}>
        <Loading label={L("NPA_LOADING")} />
      </AuthPage>
    );
  return (
    <AuthPage title={L("PWD_CHANGE_TITLE")} product={L("ADM_PRODUCT")}>
      {error ? (
        <Notice tone="error">{error}</Notice>
      ) : (
        <Notice tone="warning">{L(reason === "expired" ? "PWD_EXPIRED" : "PWD_MUST_CHANGE")}</Notice>
      )}
      <form method="post" noValidate onSubmit={(event) => void submit(event)}>
        <Field id="current" label={L("PWD_CURRENT")} error={fields.current}>
          <PasswordInput
            id="current"
            name="current"
            autoComplete="current-password"
            inputRef={current}
            error={fields.current}
            showLabel={L("LOGIN_SHOW_PASSWORD")}
            hideLabel={L("LOGIN_HIDE_PASSWORD")}
          />
        </Field>
        <Field id="new" label={L("PWD_NEW")} error={fields.new} help={L("PWD_LENGTH_RULE")}>
          <PasswordInput
            id="new"
            name="new"
            autoComplete="new-password"
            inputRef={next}
            error={fields.new}
            help={L("PWD_LENGTH_RULE")}
            showLabel={L("LOGIN_SHOW_PASSWORD")}
            hideLabel={L("LOGIN_HIDE_PASSWORD")}
          />
        </Field>
        <div className="tm-form-actions auth-actions" data-part="acciones">
          <a className="tm-btn secondary" href="/login">
            {L("ACTION_CANCEL")}
          </a>
          <button type="submit" className="tm-btn primary" aria-busy={sending ? "true" : undefined}>
            {L("PWD_SAVE")}
          </button>
        </div>
      </form>
    </AuthPage>
  );
}
